import { useState } from 'react'
import { toast } from 'sonner'
import { apiClient, useAuthStore } from '@indipia/shared'

/** 스케줄 생성 저장 공용 로직 — 공종 선택 화면(수동 배정 즉시 저장)과 배정 확인 화면(자동 배정 저장)이 함께 쓴다. */

export type SaveWork = { workTypeCode: string; duration: number; predecessorCodes?: string[] }

/** 생성 경로 — 공통코드 ZEU_SCHEDULE_ORIGIN_TYPE (MANUAL 직접 생성 · QUOTE 견적서 자동 생성) */
export type OriginType = 'MANUAL' | 'QUOTE'

export type SavePayload = {
  /** 어느 공종 선택 화면에서 왔는지(이전 버튼 복귀 대상) */
  flow: 'auto' | 'direct'
  siteId: number
  startDate: string
  endDate: string
  works: SaveWork[]
  autoAssign: boolean
  originType: OriginType
  /** 초기화(재생성) 흐름 — 기존 스케줄 교체를 이미 확인한 경우 true 로 시작 */
  force: boolean
  /** '앞으로의 기본 소요일로 저장'을 켰을 때만 채워진다(이번에 바꾼 소요일) */
  defaultDurations: { workTypeCode: string; duration: number }[]
}

type ErrShape = { response?: { status?: number; data?: { code?: string; data?: { scheduleId?: number } } } }

/** 서버 오류 코드(없으면 HTTP 상태)별 안내 문구 — 쉬운 우리말 */
export function scheduleErrorMessage(err: unknown, fallback: string): string {
  const res = (err as ErrShape)?.response
  const code = res?.data?.code
  switch (code) {
    case 'WORKS_REQUIRED':        return '공종을 한 개 이상 선택해 주세요'
    case 'DUPLICATE_WORK_TYPE':   return '같은 공종이 두 번 선택됐어요. 선택한 공종을 다시 확인해 주세요'
    case 'UNKNOWN_WORK_TYPE':     return '사용할 수 없는 공종이 포함돼 있어요. 화면을 새로고침한 뒤 다시 선택해 주세요'
    case 'DURATION_OUT_OF_RANGE': return '소요일은 1~365일이에요'
    case 'INVALID_PREDECESSOR':   return '선행 공종 지정을 확인해 주세요'
    case 'INVALID_DATE_RANGE':
    case 'INVALID_DATE':          return '공사 기간을 확인해 주세요'
    case 'INVALID_ORIGIN_TYPE':   return '스케줄 생성 경로가 올바르지 않아요. 처음부터 다시 시도해 주세요'
    case 'SCHEDULE_CREATE_CONFLICT': return '같은 현장의 스케줄이 동시에 만들어졌어요. 잠시 후 다시 시도해 주세요'
    case 'SITE_NOT_FOUND':        return '현장을 찾을 수 없어요. 현장을 다시 선택해 주세요'
  }
  if (res?.status === 404) return '현장을 찾을 수 없어요. 현장을 다시 선택해 주세요'
  if (res?.status === 409) return '같은 현장의 스케줄이 동시에 만들어졌어요. 잠시 후 다시 시도해 주세요'
  if (res?.status === 503) return '일시적인 오류예요. 잠시 후 다시 시도해 주세요'
  return fallback
}

export function useScheduleSave(onCreated: (scheduleId: number) => void) {
  const user = useAuthStore(s => s.user)
  const headers = {
    ...(user?.compId   ? { 'X-Comp-Id': String(user.compId) }   : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }
  const [saving, setSaving] = useState(false)
  // 이미 스케줄이 있는 현장(SCHEDULE_ALREADY_EXISTS) — 사용자 확인 후 force=true 로 재요청
  const [replacePayload, setReplacePayload] = useState<SavePayload | null>(null)
  // 교체 대상 기존 스케줄 ID(서버 응답 data.scheduleId) — 확인창의 고객 요청 건수 안내용
  const [replaceScheduleId, setReplaceScheduleId] = useState<number | null>(null)

  const run = async (payload: SavePayload, force: boolean) => {
    setSaving(true)
    try {
      const res = await apiClient.post<{ code: string; data: { scheduleId?: number } }>('/schedule/schedules', {
        siteId:     payload.siteId,
        startDate:  payload.startDate,
        endDate:    payload.endDate,
        works:      payload.works,
        autoAssign: payload.autoAssign,
        originType: payload.originType,
      }, { headers, params: { force } })
      if (res.data.code === 'SCHEDULE_ALREADY_EXISTS') {
        setReplaceScheduleId(res.data.data?.scheduleId ?? null)
        setReplacePayload(payload)
        return
      }
      const scheduleId = res.data.data?.scheduleId
      if (scheduleId == null) {
        toast.error('스케줄을 만들지 못했어요. 잠시 후 다시 시도해 주세요')
        return
      }
      // 공사완료 마일스톤은 서버가 생성 안에서 함께 등록한다(응답 completionMilestone) — 별도 등록 호출 없음.
      if (payload.defaultDurations.length > 0) {
        try {
          await apiClient.patch('/schedule/settings', { workTypeDurations: payload.defaultDurations }, { headers })
        } catch {
          toast.warning('스케줄은 만들었지만 기본 소요일은 저장하지 못했어요. 설정에서 다시 저장해 주세요')
        }
      }
      onCreated(scheduleId)
    } catch (err) {
      const status = (err as ErrShape)?.response
      if (status?.status === 409 && status.data?.code === 'SCHEDULE_ALREADY_EXISTS') {
        setReplaceScheduleId(status.data.data?.scheduleId ?? null)
        setReplacePayload(payload)
        return
      }
      toast.error(scheduleErrorMessage(err, '스케줄을 만들지 못했어요. 잠시 후 다시 시도해 주세요'))
    } finally {
      setSaving(false)
    }
  }

  return {
    saving,
    replaceOpen: replacePayload != null,
    replaceScheduleId,
    save: (payload: SavePayload) => run(payload, payload.force),
    confirmReplace: () => {
      const p = replacePayload
      setReplacePayload(null)
      if (p) void run(p, true)
    },
    cancelReplace: () => setReplacePayload(null),
  }
}
